/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect} from 'react'
import axios from 'axios'
import {API_URL} from '../../../modules/auth/core/_requests'
import {useAuth} from '../../../modules/auth'
import AIOptions, {AIOptionValues, defaultAIOptionValues} from './components/AIOptions'

interface VariantInfo {
  id: number
  title: string
  price: string
  sku?: string
  inventory_quantity?: number
  barcode?: string
  compare_at_price?: string
}
interface ImageInfo {
  src: string
}

export const ShopifyProductAssistantPage: FC = () => {
  const {currentUser} = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  const [activeTab, setActiveTab] = useState<'CREATE' | 'UPDATE'>('CREATE')

  // CREATE
  const [createStep, setCreateStep] = useState<number>(1)
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)
  const [createSourceUrl, setCreateSourceUrl] = useState<string>('')
  const [createOrigTitle, setCreateOrigTitle] = useState<string>('')
  const [createOrigType, setCreateOrigType] = useState<string>('')
  const [createOrigVendor, setCreateOrigVendor] = useState<string>('')
  const [createOrigTags, setCreateOrigTags] = useState<string>('')
  const [createOrigDescription, setCreateOrigDescription] = useState<string>('')
  const [createOrigVariants, setCreateOrigVariants] = useState<VariantInfo[]>([])
  const [createOrigImages, setCreateOrigImages] = useState<ImageInfo[]>([])
  const [createSeoKeywords, setCreateSeoKeywords] = useState<string>('')
  const [createAdditionalPrompt, setCreateAdditionalPrompt] = useState<string>('')
  const [createNewTitle, setCreateNewTitle] = useState<string>('')
  const [createNewDescription, setCreateNewDescription] = useState<string>('')

  // UPDATE
  const [updateStep, setUpdateStep] = useState<number>(1)
  const [updateSourceUrl, setUpdateSourceUrl] = useState<string>('')
  const [updateTargetUrl, setUpdateTargetUrl] = useState<string>('')
  const [updateOrigTitle, setUpdateOrigTitle] = useState<string>('')
  const [updateOrigType, setUpdateOrigType] = useState<string>('')
  const [updateOrigVendor, setUpdateOrigVendor] = useState<string>('')
  const [updateOrigTags, setUpdateOrigTags] = useState<string>('')
  const [updateOrigDescription, setUpdateOrigDescription] = useState<string>('')
  const [updateOrigVariants, setUpdateOrigVariants] = useState<VariantInfo[]>([])
  const [updateSeoKeywords, setUpdateSeoKeywords] = useState<string>('')
  const [updateAdditionalPrompt, setUpdateAdditionalPrompt] = useState<string>('')
  const [updateNewTitle, setUpdateNewTitle] = useState<string>('')
  const [updateNewDescription, setUpdateNewDescription] = useState<string>('')

  // New state for push checkboxes in update
  const [updatePushTitle, setUpdatePushTitle] = useState<boolean>(true)
  const [updatePushDescription, setUpdatePushDescription] = useState<boolean>(true)

  // Reset CREATE
  const resetCreate = () => {
    setCreateStep(1)
    setCreateSourceUrl('')
    setCreateOrigTitle('')
    setCreateOrigType('')
    setCreateOrigVendor('')
    setCreateOrigTags('')
    setCreateOrigDescription('')
    setCreateOrigVariants([])
    setCreateOrigImages([])
    setCreateSeoKeywords('')
    setCreateAdditionalPrompt('')
    setCreateNewTitle('')
    setCreateNewDescription('')
  }

  // Reset UPDATE
  const resetUpdate = () => {
    setUpdateStep(1)
    setUpdateSourceUrl('')
    setUpdateTargetUrl('')
    setUpdateOrigTitle('')
    setUpdateOrigType('')
    setUpdateOrigVendor('')
    setUpdateOrigTags('')
    setUpdateOrigDescription('')
    setUpdateOrigVariants([])
    setUpdateSeoKeywords('')
    setUpdateAdditionalPrompt('')
    setUpdateNewTitle('')
    setUpdateNewDescription('')
    setUpdatePushTitle(true)
    setUpdatePushDescription(true)
  }

  // Single reset handler based on active tab
  const handleResetAll = () => {
    if (activeTab === 'CREATE') {
      resetCreate()
    } else {
      resetUpdate()
    }
  }

  useEffect(() => {
    if (activeTab === 'CREATE') {
      resetUpdate()
    } else {
      resetCreate()
    }
  }, [activeTab])

  // CREATE Step 1
  const handleCreateFetchSource = async () => {
    if (!createSourceUrl) {
      alert('Please enter a source product URL.')
      return
    }
    let finalUrl = createSourceUrl
    if (!finalUrl.endsWith('.json')) {
      finalUrl += '.json'
    }
    try {
      const resp = await axios.post(`${API_URL}/shopify_product_assistant/fetch_source`, {
        sourceUrl: finalUrl,
      })
      const product = resp.data.product
      setCreateOrigTitle(product.title || '')
      setCreateOrigType(product.product_type || '')
      setCreateOrigVendor(product.vendor || '')
      setCreateOrigTags(product.tags || '')
      setCreateOrigDescription(product.body_html || '')

      const variants: VariantInfo[] = (product.variants || []).map((v: any) => ({
        id: v.id,
        title: v.title,
        price: v.price,
        sku: v.sku,
        barcode: v.barcode,
        compare_at_price: v.compare_at_price,
        inventory_quantity: v.inventory_quantity,
      }))
      setCreateOrigVariants(variants)

      const images: ImageInfo[] = (product.images || []).map((img: any) => ({
        src: img.src,
      }))
      setCreateOrigImages(images)

      setCreateStep(2)
    } catch (err) {
      console.error(err)
      alert('Failed to fetch source product.')
    }
  }

  // CREATE Step 3
  const handleCreateRewriteWithAI = async () => {
    try {
      if (!createOrigTitle) {
        alert('No product data to rewrite.')
        return
      }
      const shortTitleResp = await axios.post(`${API_URL}/shopify_product_assistant/process`, {
        originalTitle: createOrigTitle,
        originalDescription: '',
        useTitleOnly: true,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
        aiPromptExtra: createAdditionalPrompt,
      })
      setCreateNewTitle(shortTitleResp.data.optimizedTitle || '')

      const fullDescResp = await axios.post(`${API_URL}/shopify_product_assistant/process`, {
        originalTitle: createOrigDescription,
        originalDescription: createSeoKeywords,
        useDescOnly: true,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
        aiPromptExtra: createAdditionalPrompt,
      })
      setCreateNewDescription(fullDescResp.data.optimizedDescription || '')

      setCreateStep(4)
    } catch (err) {
      console.error(err)
      alert('Failed to rewrite data with AI.')
    }
  }

  // CREATE Step 4
  const handleCreatePushToShopify = async () => {
    if (!createNewTitle || !createNewDescription) {
      alert('Please confirm the new Title/Description.')
      return
    }
    try {
      const finalVariants = createOrigVariants.map((v) => {
        const tmp: any = {}
        tmp.option1 = v.title || 'Default Title'
        tmp.price = v.price || '0.00'
        if (v.sku) tmp.sku = v.sku
        if (v.barcode) tmp.barcode = v.barcode
        if (v.compare_at_price) tmp.compare_at_price = v.compare_at_price
        return tmp
      })

      await axios.post(`${API_URL}/shopify_product_assistant/create`, {
        storeName: currentStoreName,
        title: createNewTitle,
        description: createNewDescription,
        productType: createOrigType,
        variants: finalVariants,
        images: createOrigImages,
      })
      alert('New product created successfully!')
      resetCreate()
    } catch (err) {
      console.error('Create error:', err)
      alert('Failed to create product.')
    }
  }

  // UPDATE Step 1
  const handleUpdateFetchSource = async () => {
    if (!updateSourceUrl) {
      alert('Please enter a source product URL.')
      return
    }
    let finalUrl = updateSourceUrl
    if (!finalUrl.endsWith('.json')) {
      finalUrl += '.json'
    }
    try {
      const resp = await axios.post(`${API_URL}/shopify_product_assistant/fetch_source`, {
        sourceUrl: finalUrl,
      })
      const product = resp.data.product
      setUpdateOrigTitle(product.title || '')
      setUpdateOrigType(product.product_type || '')
      setUpdateOrigVendor(product.vendor || '')
      setUpdateOrigTags(product.tags || '')
      setUpdateOrigDescription(product.body_html || '')

      const variants: VariantInfo[] = (product.variants || []).map((v: any) => ({
        id: v.id,
        title: v.title,
        price: v.price,
        sku: v.sku,
        barcode: v.barcode,
        compare_at_price: v.compare_at_price,
        inventory_quantity: v.inventory_quantity,
      }))
      setUpdateOrigVariants(variants)
      setUpdateStep(2)
    } catch (err) {
      console.error(err)
      alert('Failed to fetch source product.')
    }
  }

  // UPDATE Step 4
  const handleUpdateRewriteWithAI = async () => {
    try {
      if (!updateOrigTitle) {
        alert('No product data to rewrite.')
        return
      }
      const shortTitleResp = await axios.post(`${API_URL}/shopify_product_assistant/process`, {
        originalTitle: updateOrigTitle,
        originalDescription: '',
        useTitleOnly: true,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
        aiPromptExtra: updateAdditionalPrompt,
      })
      setUpdateNewTitle(shortTitleResp.data.optimizedTitle || '')

      const fullDescResp = await axios.post(`${API_URL}/shopify_product_assistant/process`, {
        originalTitle: updateOrigDescription,
        originalDescription: updateSeoKeywords,
        useDescOnly: true,
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
        aiPromptExtra: updateAdditionalPrompt,
      })
      setUpdateNewDescription(fullDescResp.data.optimizedDescription || '')

      setUpdateStep(5)
    } catch (err) {
      console.error(err)
      alert('Failed to rewrite data with AI.')
    }
  }

  // UPDATE Step 5
  const handleUpdatePushToShopify = async () => {
    if (!updateTargetUrl) {
      alert('Please enter your store’s product URL.')
      return
    }
    if (!updatePushTitle && !updatePushDescription) {
      alert('Please select at least one field to update (Title or Description).')
      return
    }
    if (updatePushTitle && !updateNewTitle) {
      alert('Please confirm new Title.')
      return
    }
    if (updatePushDescription && !updateNewDescription) {
      alert('Please confirm new Description.')
      return
    }
    try {
      const payload: any = {
        storeName: currentStoreName,
        targetUrl: updateTargetUrl,
      }
      if (updatePushTitle) {
        payload.title = updateNewTitle
      }
      if (updatePushDescription) {
        payload.description = updateNewDescription
      }
      await axios.post(`${API_URL}/shopify_product_assistant/update`, payload)
      alert('Product updated successfully!')
      resetUpdate()
    } catch (err) {
      console.error('Update error:', err)
      alert('Failed to update product.')
    }
  }

  // Render CREATE wizard
  const renderCreateWizard = () => (
    <div className='px-3 py-3'>
      <div className='d-flex justify-content-between mb-4' style={{maxWidth: '600px'}}>
        {[1, 2, 3, 4].map((stepNum) => (
          <div
            key={stepNum}
            className={`text-center ${createStep >= stepNum ? 'fw-bold' : 'text-muted'}`}
            style={{width: '25%'}}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: createStep >= stepNum ? '#0d6efd' : '#ccc',
                color: 'white',
                margin: '0 auto',
                lineHeight: '40px',
              }}
            >
              {stepNum}
            </div>
            {stepNum === 1 && <div style={{fontSize: '0.9rem'}}>Source URL</div>}
            {stepNum === 2 && <div style={{fontSize: '0.9rem'}}>Preview</div>}
            {stepNum === 3 && <div style={{fontSize: '0.9rem'}}>Rewrite AI</div>}
            {stepNum === 4 && <div style={{fontSize: '0.9rem'}}>Push</div>}
          </div>
        ))}
      </div>

      {createStep === 1 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 1: Enter Source Product URL</h2>
          <div className='card-body'>
            <input
              type='text'
              className='form-control mb-3'
              placeholder='https://otherstore.com/products/xxxx'
              value={createSourceUrl}
              onChange={(e) => setCreateSourceUrl(e.target.value)}
            />
            <button className='btn btn-primary' onClick={handleCreateFetchSource}>
              Fetch Product
            </button>
          </div>
        </div>
      )}

      {createStep === 2 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 2: Preview Original Product</h2>
          <div className='card-body'>
            <div className='d-flex justify-content-between mb-2'>
              <span>
                <strong>Title:</strong> {createOrigTitle || '(No Title)'}
              </span>
              <button className='btn btn-sm btn-outline-primary' onClick={() => setCreateStep(1)}>
                « Back
              </button>
            </div>
            <div className='row mb-2'>
              <div className='col-md-6'>
                <p>
                  <strong>Type:</strong> {createOrigType || '-'}
                </p>
                <p>
                  <strong>Vendor:</strong> {createOrigVendor || '-'}
                </p>
                <p>
                  <strong>Tags:</strong> {createOrigTags || '-'}
                </p>
              </div>
              <div className='col-md-6'>
                <strong>Description:</strong>
                <div
                  className='mt-2 p-2 border rounded'
                  style={{maxHeight: '200px', overflowY: 'auto'}}
                  dangerouslySetInnerHTML={{__html: createOrigDescription}}
                />
              </div>
            </div>
            <hr />
            <h6>Variants</h6>
            {createOrigVariants.length === 0 && <p className='text-muted'>No variants found.</p>}
            {createOrigVariants.length > 0 && (
              <div className='table-responsive'>
                <table className='table table-sm table-bordered mt-2'>
                  <thead className='table-light'>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Price</th>
                      <th>SKU</th>
                      <th>Barcode</th>
                      <th>Compare@Price</th>
                      <th>Inventory</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createOrigVariants.map((v) => (
                      <tr key={v.id}>
                        <td>{v.id}</td>
                        <td>{v.title}</td>
                        <td>{v.price}</td>
                        <td>{v.sku || '-'}</td>
                        <td>{v.barcode || '-'}</td>
                        <td>{v.compare_at_price || '-'}</td>
                        <td>{v.inventory_quantity ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <hr />
            <h6>Images</h6>
            {createOrigImages.length === 0 && <p className='text-muted'>No images found.</p>}
            {createOrigImages.length > 0 && (
              <div className='d-flex flex-wrap'>
                {createOrigImages.map((img, i) => (
                  <div key={i} className='me-2 mb-2'>
                    <img src={img.src} alt='product' style={{width: 80, height: 80}} />
                  </div>
                ))}
              </div>
            )}
            <div className='text-end mt-3'>
              <button className='btn btn-primary' onClick={() => setCreateStep(3)}>
                Next: AI Rewrite »
              </button>
            </div>
          </div>
        </div>
      )}

      {createStep === 3 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 3: Enter SEO Keywords & Rewrite</h2>
          <div className='card-body'>
            <div className='d-flex justify-content-between mb-2'>
              <span>
                <strong>Original Title:</strong> {createOrigTitle || '(No Title)'}
              </span>
              <button className='btn btn-sm btn-outline-primary' onClick={() => setCreateStep(2)}>
                « Back
              </button>
            </div>
            <label className='form-label'>SEO Keywords (comma-separated)</label>
            <input
              type='text'
              className='form-control mb-3'
              value={createSeoKeywords}
              onChange={(e) => setCreateSeoKeywords(e.target.value)}
            />
            <label className='form-label'>Additional Prompt Info (optional)</label>
            <textarea
              className='form-control mb-3'
              rows={4}
              value={createAdditionalPrompt}
              onChange={(e) => setCreateAdditionalPrompt(e.target.value)}
            />
            <AIOptions value={aiOptions} onChange={setAiOptions} showPromptExtra={false} />
            <button className='btn btn-info' onClick={handleCreateRewriteWithAI}>
              Rewrite with AI
            </button>
          </div>
        </div>
      )}

      {createStep === 4 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 4: Final Check & Push</h2>
          <div className='card-body'>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <label className='form-label fw-bold'>New Title</label>
                <input
                  type='text'
                  className='form-control'
                  value={createNewTitle}
                  onChange={(e) => setCreateNewTitle(e.target.value)}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <label className='form-label fw-bold'>New Description (Editable)</label>
                <textarea
                  className='form-control'
                  rows={8}
                  value={createNewDescription}
                  onChange={(e) => setCreateNewDescription(e.target.value)}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <label className='form-label fw-bold'>HTML Preview</label>
                <div
                  className='border p-2 bg-light'
                  style={{minHeight: 200, maxHeight: 350, overflowY: 'auto'}}
                  dangerouslySetInnerHTML={{__html: createNewDescription}}
                />
              </div>
            </div>
            <button className='btn btn-success' onClick={handleCreatePushToShopify}>
              Push to Shopify
            </button>
          </div>
        </div>
      )}
    </div>
  )

  // UPDATE wizard
  const renderUpdateWizard = () => (
    <div className='px-3 py-3'>
      <div className='d-flex justify-content-between mb-4' style={{maxWidth: '700px'}}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div
            key={n}
            className={`text-center ${updateStep >= n ? 'fw-bold' : 'text-muted'}`}
            style={{width: '20%'}}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: updateStep >= n ? '#0d6efd' : '#ccc',
                color: 'white',
                margin: '0 auto',
                lineHeight: '40px',
              }}
            >
              {n}
            </div>
            {n === 1 && <div style={{fontSize: '0.9rem'}}>Source URL</div>}
            {n === 2 && <div style={{fontSize: '0.9rem'}}>Target URL</div>}
            {n === 3 && <div style={{fontSize: '0.9rem'}}>Preview</div>}
            {n === 4 && <div style={{fontSize: '0.9rem'}}>Rewrite AI</div>}
            {n === 5 && <div style={{fontSize: '0.9rem'}}>Push</div>}
          </div>
        ))}
      </div>

      {updateStep === 1 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 1: Enter Source Product URL</h2>
          <div className='card-body'>
            <input
              type='text'
              className='form-control mb-3'
              placeholder='https://otherstore.com/products/xxxx'
              value={updateSourceUrl}
              onChange={(e) => setUpdateSourceUrl(e.target.value)}
            />
            <button className='btn btn-primary' onClick={handleUpdateFetchSource}>
              Fetch Product
            </button>
          </div>
        </div>
      )}

      {updateStep === 2 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 2: Enter Target Product URL</h2>
          <div className='card-body'>
            <div className='d-flex justify-content-between mb-2'>
              <span>(Your store product URL to update)</span>
              <button className='btn btn-sm btn-outline-primary' onClick={() => setUpdateStep(1)}>
                « Back
              </button>
            </div>
            <input
              type='text'
              className='form-control mb-3'
              placeholder='https://yourstore.myshopify.com/products/xxxx'
              value={updateTargetUrl}
              onChange={(e) => setUpdateTargetUrl(e.target.value)}
            />
            <button className='btn btn-primary' onClick={() => setUpdateStep(3)}>
              Next: Preview »
            </button>
          </div>
        </div>
      )}

      {updateStep === 3 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 3: Preview Source Product</h2>
          <div className='card-body'>
            <div className='d-flex justify-content-between mb-2'>
              <span>
                <strong>Title:</strong> {updateOrigTitle || '(No Title)'}
              </span>
              <button className='btn btn-sm btn-outline-primary' onClick={() => setUpdateStep(2)}>
                « Back
              </button>
            </div>
            <div className='row mb-2'>
              <div className='col-md-6'>
                <p>
                  <strong>Type:</strong> {updateOrigType || '-'}
                </p>
                <p>
                  <strong>Vendor:</strong> {updateOrigVendor || '-'}
                </p>
                <p>
                  <strong>Tags:</strong> {updateOrigTags || '-'}
                </p>
              </div>
              <div className='col-md-6'>
                <strong>Description:</strong>
                <div
                  className='mt-2 p-2 border rounded'
                  style={{maxHeight: '200px', overflowY: 'auto'}}
                  dangerouslySetInnerHTML={{__html: updateOrigDescription}}
                />
              </div>
            </div>
            <hr />
            <h6>Variants</h6>
            {updateOrigVariants.length === 0 && <p className='text-muted'>No variants found.</p>}
            {updateOrigVariants.length > 0 && (
              <div className='table-responsive'>
                <table className='table table-sm table-bordered mt-2'>
                  <thead className='table-light'>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Price</th>
                      <th>SKU</th>
                      <th>Barcode</th>
                      <th>Compare@Price</th>
                      <th>Inventory</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updateOrigVariants.map((v) => (
                      <tr key={v.id}>
                        <td>{v.id}</td>
                        <td>{v.title}</td>
                        <td>{v.price}</td>
                        <td>{v.sku || '-'}</td>
                        <td>{v.barcode || '-'}</td>
                        <td>{v.compare_at_price || '-'}</td>
                        <td>{v.inventory_quantity ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className='text-end mt-3'>
              <button className='btn btn-primary' onClick={() => setUpdateStep(4)}>
                Next: AI Rewrite »
              </button>
            </div>
          </div>
        </div>
      )}

      {updateStep === 4 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 4: Enter SEO Keywords & Rewrite</h2>
          <div className='card-body'>
            <div className='d-flex justify-content-between mb-2'>
              <span>
                <strong>Original Title:</strong> {updateOrigTitle || '(No Title)'}
              </span>
              <button className='btn btn-sm btn-outline-primary' onClick={() => setUpdateStep(3)}>
                « Back
              </button>
            </div>
            <label className='form-label'>SEO Keywords (comma-separated)</label>
            <input
              type='text'
              className='form-control mb-3'
              value={updateSeoKeywords}
              onChange={(e) => setUpdateSeoKeywords(e.target.value)}
            />
            <label className='form-label'>Additional Prompt Info (optional)</label>
            <textarea
              className='form-control mb-3'
              rows={4}
              value={updateAdditionalPrompt}
              onChange={(e) => setUpdateAdditionalPrompt(e.target.value)}
            />
            <AIOptions value={aiOptions} onChange={setAiOptions} showPromptExtra={false} />
            <button className='btn btn-info' onClick={handleUpdateRewriteWithAI}>
              Rewrite with AI
            </button>
          </div>
        </div>
      )}

      {updateStep === 5 && (
        <div className='card mt-3'>
          <h2 className='mt-3 ms-3'>Step 5: Final Check & Update</h2>
          <div className='card-body'>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <div className='form-check mb-2'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id='updatePushTitleCheckbox'
                    checked={updatePushTitle}
                    onChange={(e) => setUpdatePushTitle(e.target.checked)}
                  />
                  <label className='form-check-label' htmlFor='updatePushTitleCheckbox'>
                    Push Title
                  </label>
                </div>
                <label className='form-label fw-bold'>New Title</label>
                <input
                  type='text'
                  className='form-control'
                  value={updateNewTitle}
                  onChange={(e) => setUpdateNewTitle(e.target.value)}
                />
              </div>
            </div>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <div className='form-check mb-2'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id='updatePushDescriptionCheckbox'
                    checked={updatePushDescription}
                    onChange={(e) => setUpdatePushDescription(e.target.checked)}
                  />
                  <label className='form-check-label' htmlFor='updatePushDescriptionCheckbox'>
                    Push Description
                  </label>
                </div>
                <label className='form-label fw-bold'>New Description (Editable)</label>
                <textarea
                  className='form-control'
                  rows={8}
                  value={updateNewDescription}
                  onChange={(e) => setUpdateNewDescription(e.target.value)}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <label className='form-label fw-bold'>HTML Preview</label>
                <div
                  className='border p-2 bg-light'
                  style={{minHeight: 200, maxHeight: 350, overflowY: 'auto'}}
                  dangerouslySetInnerHTML={{__html: updateNewDescription}}
                />
              </div>
            </div>
            <button className='btn btn-success' onClick={handleUpdatePushToShopify}>
              Push Update to Shopify
            </button>
          </div>
        </div>
      )}
    </div>
  )

  const renderTopBar = () => {
    let sourceUrlToShow = ''
    if (activeTab === 'CREATE' && createSourceUrl) {
      sourceUrlToShow = createSourceUrl
    } else if (activeTab === 'UPDATE' && updateSourceUrl) {
      sourceUrlToShow = updateSourceUrl
    }
    return (
      <div className='d-flex align-items-center justify-content-between mt-3 ms-3 me-3'>
        <h1 className='mb-0'>
          Shopify Product Assistant – {currentStoreName}
          {sourceUrlToShow && <span className='ms-3 text-muted'>Source: {sourceUrlToShow}</span>}
        </h1>
        <button className='btn btn-warning' onClick={handleResetAll}>
          Reset Wizard
        </button>
      </div>
    )
  }

  return (
    <>
      {renderTopBar()}
      <ul className='nav nav-tabs mt-4 ms-3'>
        <li className='nav-item'>
          <a
            className={`nav-link ${activeTab === 'CREATE' ? 'active' : ''}`}
            onClick={() => setActiveTab('CREATE')}
          >
            Create New Product
          </a>
        </li>
        <li className='nav-item'>
          <a
            className={`nav-link ${activeTab === 'UPDATE' ? 'active' : ''}`}
            onClick={() => setActiveTab('UPDATE')}
          >
            Update Existing Product
          </a>
        </li>
      </ul>

      {activeTab === 'CREATE' && renderCreateWizard()}
      {activeTab === 'UPDATE' && renderUpdateWizard()}
    </>
  )
}

export const ShopifyProductAssistantWrapper: FC = () => {
  const {currentUser} = useAuth()
  return (
    <>
      <ShopifyProductAssistantPage />
    </>
  )
}
